/**
 * RuleSelectionStep Component
 * Step 2 of the deployment wizard - select rules per tenant
 */

import React, { useMemo, useCallback } from 'react';
import {
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { extractSitNames, extractLabelNames, TenantDeployment, TenantRuleStatus } from '../types';
import { RuleRow, SelectionListPanel, DialogFooter, SitLabelGrid, WizardDialog } from '../shared';
import '../shared/ruleSelection.css';
import type { RuleSelectionStepProps } from './types';

export const RuleSelectionStep: React.FC<RuleSelectionStepProps> = ({
  policy,
  wizardState,
  getTenantNameById,
  getActiveRuleIds,
  onRuleToggle,
  onSetActiveTenant,
  onToggleRuleExpand,
  onBack,
  onNext,
  onClose,
}) => {
  const corpRules = policy.rules;
  const tenantIds = Array.from(wizardState.selectedTenantIds);
  const activeRuleIds = getActiveRuleIds();
  const hasAnySelection = activeRuleIds.size > 0;

  // Check if all tenants have at least one rule selected
  const allTenantsHaveRules = tenantIds.every(tid => {
    const ruleIds = wizardState.tenantRuleSelections.get(tid);
    return ruleIds && ruleIds.size > 0;
  });

  // Helper to get tenant deployment percent
  const getTenantDeployPercent = useCallback((tenantId: string) => {
    const totalRules = corpRules.length;
    if (totalRules === 0) return 0;
    const tenantDeployment = policy.tenantDeployments.find(
      (td: TenantDeployment) => td.tenantId.toLowerCase() === tenantId.toLowerCase()
    );
    const corpRuleIds = new Set(corpRules.map(r => r.ruleId));
    const deployedCount = tenantDeployment?.ruleStatuses.filter(
      (rs: TenantRuleStatus) => rs.isDeployed && corpRuleIds.has(rs.ruleId)
    ).length || 0;
    return Math.min((deployedCount / totalRules) * 100, 100);
  }, [corpRules, policy.tenantDeployments]);

  // Transform tenants for SelectionListPanel
  const listItems = useMemo(() => {
    const sortedTenantIds = [...tenantIds].sort((a, b) => {
      return getTenantDeployPercent(a) - getTenantDeployPercent(b);
    });

    return sortedTenantIds.map(tenantId => {
      const tenantRules = wizardState.tenantRuleSelections.get(tenantId);
      const ruleCount = tenantRules?.size || 0;

      return {
        id: tenantId,
        name: getTenantNameById(tenantId),
        subtitle: `${ruleCount} rule${ruleCount !== 1 ? 's' : ''} selected`,
        deployPercent: getTenantDeployPercent(tenantId),
        hasError: ruleCount === 0,
      };
    });
  }, [tenantIds, wizardState.tenantRuleSelections, getTenantNameById]);

  // Check if a rule is deployed for the active tenant
  const isRuleDeployed = (ruleId: string) => {
    const tenantDeployment = policy.tenantDeployments.find(
      (td: TenantDeployment) => td.tenantId.toLowerCase() === wizardState.activeTenantId.toLowerCase()
    );
    return tenantDeployment?.ruleStatuses.some(
      (rs: TenantRuleStatus) => rs.ruleId === ruleId && rs.isDeployed
    ) || false;
  };

  return (
    <WizardDialog
      title={`Compare and Deploy - ${policy.policyName}`}
      onClose={onClose}
      contentStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'clamp(300px, 65vh, 800px)' }}
    >
            {/* Two panel layout */}
            <div className="rsp-container">
              {/* Left Panel - Tenant List */}
              <SelectionListPanel
                headerText={`Tenants (${tenantIds.length})`}
                items={listItems}
                activeId={wizardState.activeTenantId}
                onItemClick={onSetActiveTenant}
              />

              {/* Right Panel - Rules for selected tenant */}
              <div className="rsp-right-panel">
                <div className="rsp-rules-header">
                  Corp Policy Rules ({corpRules.length})
                </div>

                {!hasAnySelection && (
                  <MessageBar intent="warning" style={{ margin: '12px 16px' }}>
                    <MessageBarBody>Please select at least one rule for this tenant.</MessageBarBody>
                  </MessageBar>
                )}

                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {corpRules.map((rule) => {
                    const sitNames = extractSitNames(rule.sits);
                    const labelNames = extractLabelNames(rule.labels);
                    const isDeployed = isRuleDeployed(rule.ruleId);

                    return (
                      <RuleRow
                        key={rule.ruleId}
                        rule={{
                          id: rule.ruleId,
                          name: rule.ruleName,
                          description: '',
                          priority: 'Medium',
                          status: 'Enabled',
                          sitNames,
                          labelNames,
                        }}
                        isExpanded={wizardState.expandedRuleIds.has(rule.ruleId)}
                        onToggle={() => onToggleRuleExpand(rule.ruleId)}
                        showCheckbox={true}
                        isChecked={activeRuleIds.has(rule.ruleId)}
                        onCheckChange={(checked) => onRuleToggle(rule.ruleId, checked)}
                        showDeploymentTag={true}
                        isDeployed={isDeployed}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <DialogFooter
              showBack
              onBack={onBack}
              onCancel={onClose}
              onPrimary={onNext}
              primaryLabel="Next"
              primaryDisabled={!allTenantsHaveRules}
            />
    </WizardDialog>
  );
};

export default RuleSelectionStep;
